import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

// Custom CSS for line-clamp
const lineClampStyle = `
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const NameCheck = () => {
  const [name, setName] = useState('');
  const [trademarkClass, setTrademarkClass] = useState('');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [companyTypeFilter, setCompanyTypeFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Toggle expanded state for a specific row
  const toggleExpanded = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Status options for filter
  const statusOptions = [
    'ABA/Readvertised',
    'Abandoned',
    'Acceptance Withdrawn Process',
    'Accepted',
    'Accepted & Advertised',
    'Accepted/Readvertise',
    'Advertised bef acc',
    'Cancelled',
    'Exam Report Issued',
    'Formalities Chk Fail',
    'Formalities Chk Pass',
    'Fresh After Payment',
    'INVALID (No fee received)',
    'Marked for Exam',
    'New Application',
    'Objected',
    'Online New Appl.',
    'Opposed',
    'Opposed*',
    'Rectification Filed',
    'Refused',
    'Registered',
    'Removed',
    'Send Back to EDP',
    'Send to PRAS',
    'Send To Vienna Codification',
    'Split',
    'Withdrawn'
  ];

  // Company type options
  const companyTypeOptions = [
    { value: '', label: 'All' },
    { value: 'Limited', label: 'Limited' },
    { value: 'Private Limited', label: 'Private Limited' }
  ];

  // Generate year options for date filters
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = currentYear; i >= 1900; i--) {
    yearOptions.push(i);
  }

  const handleSearch = async () => {
    if (!name.trim() || !trademarkClass) {
      toast.error('Please enter a name and select a class');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/name-check/search', {
        name: name.trim(),
        class: trademarkClass,
        date: date || undefined
      });

      if (response.data.success) {
        setResults(response.data.data);
        setShowFilters(true);
        toast.success('Name check completed successfully!');
      } else {
        toast.error(response.data.message || 'Search failed');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.message || 'Failed to perform name check');
    } finally {
      setLoading(false);
    }
  };

  const handleLiveInfo = async (appno) => {
    setModalLoading(true);
    setShowModal(true);
    
    try {
      const response = await api.get(`/name-check/live-info/${appno}`);
      
      if (response.data.success) {
        setModalData(response.data.data);
        toast.success('Live info retrieved successfully!');
      } else {
        toast.error(response.data.message || 'Failed to get live info');
        setShowModal(false);
      }
    } catch (error) {
      console.error('Live info error:', error);
      toast.error(error.response?.data?.message || 'Failed to get live info');
      setShowModal(false);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalData(null);
  };

  const filterResults = (data, category) => {
    if (!data || !data[category]) return [];

    let filtered = [...data[category]];

    // Status filter
    if (statusFilter.length > 0) {
      filtered = filtered.filter(item => 
        statusFilter.includes(item.status)
      );
    }

    // Company type filter
    if (companyTypeFilter) {
      filtered = filtered.filter(item => 
        item.company_type === companyTypeFilter
      );
    }

    // Date filter
    if (dateFromFilter || dateToFilter) {
      filtered = filtered.filter(item => {
        if (!item.dateOfAppStr || item.dateOfAppStr === '-') return false;
        
        const itemYear = parseInt(item.dateOfAppStr.split('/')[2]);
        const fromYear = dateFromFilter ? parseInt(dateFromFilter) : 1900;
        const toYear = dateToFilter ? parseInt(dateToFilter) : currentYear;
        
        return itemYear >= fromYear && itemYear <= toYear;
      });
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Registered': 'bg-green-100 text-green-800',
      'Objected': 'bg-red-100 text-red-800',
      'Abandoned': 'bg-gray-100 text-gray-800',
      'Withdrawn': 'bg-gray-100 text-gray-800',
      'Removed': 'bg-gray-100 text-gray-800',
      'Accepted': 'bg-blue-100 text-blue-800',
      'Opposed': 'bg-orange-100 text-orange-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getScoreColor = (score) => {
    const scoreColors = {
      'very high': 'bg-red-100 text-red-800',
      'high': 'bg-orange-100 text-orange-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };
    return scoreColors[score] || 'bg-gray-100 text-gray-800';
  };

  const renderTable = (data, category, title) => {
    const filteredData = filterResults(data, category);
    
    if (filteredData.length === 0) return null;

    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">{title} ({filteredData.length})</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 border-b text-left">Appno</th>
                <th className="px-4 py-2 border-b text-left">Applied For</th>
                <th className="px-4 py-2 border-b text-left">Status</th>
                <th className="px-4 py-2 border-b text-left">Company name</th>
                <th className="px-4 py-2 border-b text-left">Company type</th>
                <th className="px-4 py-2 border-b text-left">Goods and Service</th>
                <th className="px-4 py-2 border-b text-left">Class</th>
                <th className="px-4 py-2 border-b text-left">Valid up to</th>
                <th className="px-4 py-2 border-b text-left">State</th>
                <th className="px-4 py-2 border-b text-left">User detail</th>
                <th className="px-4 py-2 border-b text-left">Date of app</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, index) => {
                const rowId = `${category}-${item.appno}-${index}`;
                const isExpanded = expandedRows.has(rowId);
                const goodsAndService = item.goodsAndSerice || '';
                const shouldShowToggle = goodsAndService.length > 100;
                
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">
                      <button
                        onClick={() => handleLiveInfo(item.appno)}
                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                      >
                        {item.appno}
                      </button>
                    </td>
                    <td className="px-4 py-2 border-b">{item.tmAppliedFor}</td>
                    <td className="px-4 py-2 border-b">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 border-b">{item.companyName}</td>
                    <td className="px-4 py-2 border-b">{item.company_type}</td>
                    <td className="px-4 py-2 border-b max-w-xs">
                      <div className="whitespace-pre-wrap">
                        {shouldShowToggle ? (
                          <div>
                            <div className={`${isExpanded ? '' : 'line-clamp-2'} text-sm`}>
                              {goodsAndService}
                            </div>
                            <button
                              onClick={() => toggleExpanded(rowId)}
                              className="text-blue-600 hover:text-blue-800 text-xs mt-1 underline"
                            >
                              {isExpanded ? 'Show Less' : 'Show More'}
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm">{goodsAndService}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 border-b">{item.class}</td>
                    <td className="px-4 py-2 border-b">{item.validUpto}</td>
                    <td className="px-4 py-2 border-b">{item.state}</td>
                    <td className="px-4 py-2 border-b">{item.userDetail}</td>
                    <td className="px-4 py-2 border-b">{item.dateOfAppStr}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Name Check</h1>
        </div>

        {/* Search Form */}
        <div className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trademark Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter trademark name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class
              </label>
              <select
                value={trademarkClass}
                onChange={(e) => setTrademarkClass(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Class</option>
                {Array.from({ length: 45 }, (_, i) => i + 1).map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
                <option value="99">99</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Journal Date (Optional)
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                disabled={loading}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && results && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  multiple
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(Array.from(e.target.selectedOptions, option => option.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Type
                </label>
                <select
                  value={companyTypeFilter}
                  onChange={(e) => setCompanyTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {companyTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Year
                </label>
                <select
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Any</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Year
                </label>
                <select
                  value={dateToFilter}
                  onChange={(e) => setDateToFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Any</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {results && (
          <div>
            {/* Summary Statistics */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-800">Very High Risk</h4>
                <p className="text-2xl font-bold text-red-600">{results.summary?.vhigh || 0}</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-800">High Risk</h4>
                <p className="text-2xl font-bold text-orange-600">{results.summary?.high || 0}</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold text-yellow-800">Medium Risk</h4>
                <p className="text-2xl font-bold text-yellow-600">{results.summary?.medium || 0}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800">Low Risk</h4>
                <p className="text-2xl font-bold text-green-600">{results.summary?.low || 0}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800">Other</h4>
                <p className="text-2xl font-bold text-gray-600">{results.summary?.other || 0}</p>
              </div>
            </div>

            {/* Results Tables */}
            {renderTable(results, 'vhigh', 'Very High Risk')}
            {renderTable(results, 'high', 'High Risk')}
            {renderTable(results, 'medium', 'Medium Risk')}
            {renderTable(results, 'low', 'Low Risk')}
            {renderTable(results, 'other', 'Other')}
          </div>
        )}
      </div>

      {/* Live Info Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {modalLoading ? 'Loading...' : `Live Information - App No #${modalData?.appno || ''}`}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="p-6">
              {modalLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : modalData ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 border-b text-center" colSpan="2">
                          Appno #{modalData.appno}
                        </th>
                        <th className="px-4 py-2 border-b text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(modalData.status)}`}>
                            {modalData.status}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">DateOfApp</td>
                        <td className="px-4 py-2 border-b" colSpan="2">
                          {modalData.dateOfAppStr || modalData.dateOfApp || '-'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">Class</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.class || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">State</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.state || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">Country</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.country || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">TmAppliedFor</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.tmAppliedFor || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">UserDetail</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.userDetail || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">ValidUpto</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.validUpto || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">PropName</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.propName || modalData.companyName || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">PropAddress</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.propAddress || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">AgentName</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.agentName || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">BuisnessName</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.buisnessName || modalData.companyName || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">BuisnessType</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.buisnessType || modalData.company_type || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">GoodsAndSerice</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.goodsAndSerice || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">Imgurl</td>
                        <td className="px-4 py-2 border-b text-center" colSpan="2">
                          {modalData.imgurl ? (
                            <img src={modalData.imgurl} alt="Trademark" className="max-w-xs mx-auto" />
                          ) : '-'}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">Updatedate</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.updatedate || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">Tmcategory</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.tmcategory || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">Tmtype</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.tmtype || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">Restr</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.restr || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">Pubdate</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.pubdate || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">JournalNo</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.JournalNo || modalData.jnumber || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">JournalDate</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.JournalDate || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">Regdate</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.regdate || '-'}</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">DateofHearing</td>
                        <td className="px-4 py-2 border-b" colSpan="2">{modalData.DateofHearing || '-'}</td>
                      </tr>
                      
                      {/* Documents Section */}
                      {modalData.doc_name && modalData.doc_name.length > 0 && (
                        <tr>
                          <td className="px-4 py-2 border-b font-medium">DocUrl</td>
                          <td className="px-4 py-2 border-b" colSpan="2">
                            <div className="overflow-x-auto">
                              <table className="min-w-full border border-gray-300">
                                <tbody>
                                  {modalData.doc_name.map((docName, index) => (
                                    <tr key={index}>
                                      <td className="px-2 py-1 border-b">
                                        {modalData.doc_date && modalData.doc_date[index] ? modalData.doc_date[index] : '-'}
                                      </td>
                                      <td className="px-2 py-1 border-b">
                                        {modalData.doc_url && modalData.doc_url[index] ? (
                                          <a 
                                            href={modalData.doc_url[index]} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 underline"
                                          >
                                            {docName}
                                          </a>
                                        ) : (
                                          docName
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                      
                      <tr>
                        <td className="px-4 py-2 border-b font-medium">ExamUrl</td>
                        <td className="px-4 py-2 border-b" colSpan="2">
                          {modalData.exam_url && modalData.exam_url.length > 0 ? (
                            <a 
                              href={modalData.exam_url[0]} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              Examination Report
                            </a>
                          ) : '-'}
                        </td>
                      </tr>
                      
                      {/* Opposition Details */}
                      {modalData.oppnum && modalData.oppnum.length > 0 && (
                        <>
                          <tr>
                            <td className="px-4 py-2 border-b font-medium">Oppnum</td>
                            <td className="px-4 py-2 border-b" colSpan="2">{modalData.oppnum[0] || '-'}</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 border-b font-medium">Oppcode</td>
                            <td className="px-4 py-2 border-b" colSpan="2">{modalData.oppcode && modalData.oppcode[0] || '-'}</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 border-b font-medium">Oppdate</td>
                            <td className="px-4 py-2 border-b" colSpan="2">{modalData.oppdate && modalData.oppdate[0] || '-'}</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 border-b font-medium">Oppname</td>
                            <td className="px-4 py-2 border-b" colSpan="2">
                              {modalData.oppname && modalData.oppname.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full border border-gray-300">
                                    <tbody>
                                      {modalData.oppname.map((oppName, index) => (
                                        <tr key={index}>
                                          <td className="px-2 py-1">{oppName}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : '-'}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NameCheck; 